<div class="modal-overlay" (click)="onCancel()">
  <div
    class="modal-container"
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-discount-title"
    (click)="$event.stopPropagation()"
  >
    <div class="modal-header">
      <h2 class="modal-title" id="create-discount-title">{{ title }}</h2>
      <button type="button" class="close-button" aria-label="Close" (click)="onCancel()">
        &times;
      </button>
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="discount-form">
      <div class="modal-body">
        <!-- Discount Details -->
        <section class="form-section">
          <h3 class="section-title">Discount Details</h3>

          <div class="form-section__fields">
            <div class="form-row">
              <div class="form-group">
                <label for="discountName">Discount Name<span class="required">*</span></label>
                <input
                  id="discountName"
                  type="text"
                  formControlName="name"
                  placeholder="e.g. Loyalty Reward"
                  [maxlength]="nameMaxLength"
                />
                <div class="error-message" *ngIf="submitted && form.get('name')?.invalid">
                  Discount name is required
                </div>
              </div>

              <div class="form-group status-group">
                <label>Status</label>
                <div class="status-toggle">
                  <label class="switch">
                    <input type="checkbox" formControlName="is_active" />
                    <span class="slider round"></span>
                  </label>
                  <span>{{ form.get('is_active')?.value ? 'Active' : 'Inactive' }}</span>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label id="discount-location-label">Location<span class="required">*</span></label>
              <app-checkbox-multi-dropdown
                [options]="locationOptions"
                [selectedIds]="form.get('location_ids')?.value || []"
                appearance="input"
                [checkboxSize]="checkboxSize"
                [showFooter]="false"
                [showAllOption]="false"
                [fixedOverlay]="true"
                [footerAction]="clearFooterAction"
                placeholder="Select Location"
                ariaLabelledBy="discount-location-label"
                (selectedIdsChange)="onLocationsChange($event)"
                (footerActionClick)="onLocationsChange([])"
              ></app-checkbox-multi-dropdown>
              <div class="error-message" *ngIf="submitted && form.get('location_ids')?.invalid">
                Location is required
              </div>
            </div>

            <div
              class="form-row form-row--value"
              [class.is-superseded]="isCustomScheduleMode"
              [attr.inert]="isCustomScheduleMode ? '' : null"
            >
              <div class="form-group form-group--type">
                <label>Discount Type<span class="required">*</span></label>
                <mat-radio-group class="radio-button-group" formControlName="discount_type">
                  <div class="radio-option" *ngFor="let option of discountTypeOptions">
                    <mat-radio-button
                      [value]="option.value"
                      [inputId]="'discount_type_' + option.value"
                    ></mat-radio-button>
                    <label [for]="'discount_type_' + option.value">{{ option.label }}</label>
                  </div>
                </mat-radio-group>
              </div>

              <div class="form-group form-group--value">
                <label for="discountValue">Discount Value<span class="required">*</span></label>
                <app-currency-action-input
                  inputId="discountValue"
                  formControlName="discount_value"
                  size="compact"
                  [showAction]="false"
                  [allowDecimal]="!isPercentage"
                  [allowNull]="true"
                  [showPrefix]="!isPercentage"
                  [suffix]="isPercentage ? '%' : ''"
                  [maxInput]="isPercentage ? '100' : '999999.99'"
                  [placeholder]="isPercentage ? '10' : '100'"
                ></app-currency-action-input>
              </div>
            </div>
          </div>
        </section>

        <!-- Eligibility -->
        <section class="form-section">
          <h3 class="section-title">Eligibility</h3>
          <div class="pill-group">
            <button
              type="button"
              class="pill"
              *ngFor="let option of eligibilityOptions"
              [class.pill--active]="form.get('eligibility')?.value === option.value"
              (click)="onEligibilitySelect(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </section>

        <!-- Schedule -->
        <section class="form-section form-section--schedule">
          <h3 class="section-title">Schedule</h3>

          <div class="form-section__fields form-section__fields--tight">
            <app-checkbox
              class="form-toggle-checkbox"
              [checked]="alwaysActive"
              [size]="checkboxSize"
              label="Always Active"
              (checkedChange)="setAlwaysActive($event)"
            ></app-checkbox>

            <div class="form-row schedule-dates" [class.schedule-dates--disabled]="alwaysActive">
              <div class="form-group">
                <label id="discount-start-date-label" class="sr-only">Start Date & Time</label>
                <app-custom-date-picker
                  formControlName="start_date"
                  placeholder="Start Date & Time"
                  mode="datetime"
                  [showTime]="true"
                  [allowPastDates]="true"
                  [locationDetails]="scheduleShopClock"
                  [minDate]="minStartDate"
                  [fixedOverlay]="true"
                  (dateTimeChange)="onStartDateChange($event)"
                ></app-custom-date-picker>
              </div>
              <div class="form-group">
                <label id="discount-end-date-label" class="sr-only">End Date & Time</label>
                <app-custom-date-picker
                  formControlName="end_date"
                  placeholder="End Date & Time"
                  mode="datetime"
                  [showTime]="true"
                  [allowPastDates]="true"
                  [locationDetails]="scheduleShopClock"
                  [minDate]="minEndDate"
                  [fixedOverlay]="true"
                  dropdownAlign="right"
                ></app-custom-date-picker>
              </div>
            </div>
          </div>
        </section>

        <!-- Restriction -->
        <section class="form-section">
          <h3 class="section-title">Restriction</h3>

          <div class="form-row">
            <div class="form-group">
              <label id="discount-days-label">Day of Week</label>
              <app-checkbox-multi-dropdown
                [options]="weekDays"
                [selectedIds]="form.get('restrict_days_of_week')?.value || []"
                appearance="input"
                [checkboxSize]="checkboxSize"
                [showFooter]="false"
                [showAllOption]="false"
                [fixedOverlay]="true"
                [footerAction]="clearFooterAction"
                placeholder="Select Days"
                ariaLabelledBy="discount-days-label"
                (selectedIdsChange)="onDaysChange($event)"
                (footerActionClick)="onDaysChange([])"
              ></app-checkbox-multi-dropdown>
            </div>

            <div class="form-group time-range-group">
              <label>Time range</label>
              <div class="time-range">
                <app-custom-date-picker
                  mode="time"
                  placeholder="From"
                  [selectedTimeSlot]="form.get('restrict_from_time')?.value || ''"
                  [showFooterButtons]="false"
                  [showClear]="true"
                  [allowPastDates]="true"
                  [fixedOverlay]="true"
                  (timeSlotChange)="onFromTimeSelect($event)"
                ></app-custom-date-picker>

                <app-custom-date-picker
                  mode="time"
                  placeholder="To"
                  [selectedTimeSlot]="form.get('restrict_to_time')?.value || ''"
                  [minDate]="minToDate"
                  [showFooterButtons]="false"
                  [showClear]="true"
                  [allowPastDates]="true"
                  [fixedOverlay]="true"
                  dropdownAlign="right"
                  (timeSlotChange)="onToTimeSelect($event)"
                ></app-custom-date-picker>
              </div>
            </div>
          </div>
        </section>

        <!-- Applicability -->
        <section class="form-section">
          <h3 class="section-title">Applicability</h3>

          <div class="form-row">
            <div class="form-group">
              <label id="discount-wash-type-label">Wash Type<span class="required">*</span></label>
              <app-checkbox-multi-dropdown
                [options]="washTypeOptions"
                [selectedIds]="form.get('wash_types')?.value || []"
                appearance="input"
                [checkboxSize]="checkboxSize"
                [showFooter]="false"
                [showAllOption]="true"
                [selectAllEmitsIds]="true"
                allOptionLabel="All wash"
                [fixedOverlay]="true"
                [footerAction]="clearFooterAction"
                placeholder="Select Wash Type"
                ariaLabelledBy="discount-wash-type-label"
                (selectedIdsChange)="onWashTypesChange($event)"
                (footerActionClick)="onWashTypesChange([])"
              ></app-checkbox-multi-dropdown>
              <div class="error-message" *ngIf="submitted && form.get('wash_types')?.invalid">
                Wash Type is required
              </div>
            </div>

            <div class="form-group">
              <label id="discount-package-label">Wash Package<span class="required">*</span></label>
              <app-checkbox-multi-dropdown
                [options]="packageOptions"
                [selectedIds]="form.get('package_ids')?.value || []"
                appearance="input"
                [checkboxSize]="checkboxSize"
                [showFooter]="false"
                [showAllOption]="false"
                [fixedOverlay]="true"
                [loading]="loadingPackages"
                [disabled]="isPackageDropdownDisabled"
                [placeholder]="packagePlaceholder"
                [footerAction]="clearFooterAction"
                emptyMessage="No packages available for the selected wash type"
                ariaLabelledBy="discount-package-label"
                (selectedIdsChange)="onPackagesChange($event)"
                (footerActionClick)="onPackagesChange([])"
              ></app-checkbox-multi-dropdown>
              <div class="error-message" *ngIf="submitted && form.get('package_ids')?.invalid">
                Wash Package is required
              </div>
            </div>
          </div>
        </section>

        <!-- Advance Renewal Cycle: monthly-membership-only discounts (API rule) -->
        <section class="renewal-panel" *ngIf="isMonthlyMembershipOnly">
          <h4 class="renewal-panel__title">Advance Renewal Cycle</h4>
          <p class="renewal-panel__hint">
            By default, the discount above applies once at signup and every renewal bills at full
            price. Pick an option below only if renewals need different treatment.
          </p>

          <div class="renewal-cards">
            <button
              type="button"
              class="renewal-card"
              *ngFor="let card of renewalCards"
              [class.renewal-card--active]="renewalCycle === card.value"
              [attr.aria-pressed]="renewalCycle === card.value"
              (click)="onSelectRenewalCycle(card.value)"
            >
              <span class="renewal-card__title">{{ card.title }}</span>
              <span class="renewal-card__description">{{ card.description }}</span>
            </button>
          </div>

          <!-- Recurring: how many months the same discount keeps applying. -->
          <div class="recurring-months" *ngIf="showRecurringMonths">
            <label class="recurring-months__label" for="recurringMonths">
              Select number of month<span class="required">*</span>
            </label>
            <div class="recurring-months__field">
              <input
                id="recurringMonths"
                type="number"
                min="1"
                [max]="maxRecurringMonths"
                step="1"
                formControlName="recurring_renewal_months"
                placeholder="1"
                (input)="onRecurringMonthsInput($event)"
                (keydown)="blockNonIntegerKeys($event)"
              />
              <span class="recurring-months__unit">month</span>
            </div>
            <p class="recurring-months__hint">
              The discount repeats for up to {{ maxRecurringMonths }} months of billing, starting
              with the first purchase.
            </p>
          </div>

          <div class="renewal-rows" *ngIf="showCustomScheduleRows">
            <!--
              The signup charge. It edits the same discount_type/discount_value controls as
              Section A, surfaced here so the whole schedule reads top to bottom; Section A
              is faded while this row is showing.
            -->
            <div class="renewal-row renewal-row--first-purchase">
              <span class="renewal-row__label">First purchase discount</span>

              <select formControlName="discount_type" aria-label="First purchase discount type">
                <option value="percentage">%</option>
                <option value="flat">$</option>
              </select>

              <input
                type="number"
                min="0"
                [max]="isPercentage ? 100 : null"
                step="0.01"
                formControlName="discount_value"
                aria-label="First purchase discount value"
                [placeholder]="isPercentage ? '10' : '100'"
              />

              <span class="renewal-row__spacer" aria-hidden="true"></span>
            </div>

            <div formArrayName="custom_schedules">
              <div
                class="renewal-row"
                *ngFor="let scheduleControl of customSchedules.controls; let i = index"
                [formGroupName]="i"
              >
                <span class="renewal-row__label">Renewal {{ i + 1 }}</span>

                <select formControlName="discount_type" aria-label="Renewal discount type">
                  <option value="percentage">%</option>
                  <option value="flat">$</option>
                </select>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  formControlName="discount_value"
                  aria-label="Renewal discount value"
                  placeholder="50"
                />

                <button
                  type="button"
                  class="renewal-row__remove"
                  [attr.aria-label]="'Remove renewal ' + (i + 1)"
                  (click)="removeRenewal(i)"
                >
                  &times;
                </button>
              </div>
            </div>

            <button
              type="button"
              class="add-renewal"
              [disabled]="!canAddRenewal"
              (click)="addRenewal()"
            >
              + Add Renewal
            </button>

            <p class="renewal-note">
              Any renewal not listed above bills at full price. Maximum {{ maxRenewals }} renewals
              can be configured.
            </p>
          </div>
        </section>

        <!-- Minimum purchase -->
        <div class="minimum-purchase-section">
          <app-checkbox
            class="form-toggle-checkbox"
            [checked]="addMinimumPurchase"
            [size]="checkboxSize"
            label="Add Minimum Purchase Amount"
            (checkedChange)="setMinimumPurchase($event)"
          ></app-checkbox>

          <div class="form-group minimum-purchase-field" *ngIf="addMinimumPurchase">
            <label for="minimumPurchase">Minimum Purchase Amount</label>
            <app-currency-action-input
              inputId="minimumPurchase"
              formControlName="minimum_purchase_amount"
              size="compact"
              [showAction]="false"
              [allowDecimal]="true"
              [allowNull]="true"
              maxInput="999999.99"
              placeholder="0"
            ></app-currency-action-input>
          </div>
        </div>
      </div>

      <app-modal-footer-actions
        secondaryText="Clear"
        cancelText="Cancel"
        [primaryText]="primaryLabel"
        [primaryLoading]="submitting"
        primaryLoadingText="Saving..."
        [primaryDisabled]="isPrimaryDisabled"
        primaryButtonType="submit"
        (secondaryClick)="onClear()"
        (cancelClick)="onCancel()"
      ></app-modal-footer-actions>
    </form>
  </div>
</div>
